import { Component, inject, Input, OnDestroy, OnInit, viewChild } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { GoogleMapsModule } from "@angular/google-maps";
import { GoogleMapsLoaderService } from "@services/google-maps-loader.service";
import { GoogleMapsService } from "@services/google-maps.service";
import { AddressFormComponent } from "@shared/components/forms/address-form/address-form.component";
import { AddressFormGroup } from "@shared/components/forms/address-form/address-form.form";
import {
  debounceTime,
  filter,
  first,
  map,
  Subject,
  switchMap,
  takeUntil,
} from "rxjs";
import { environment } from "../../../../../../environments/environment";
import { ButtonComponent } from "../../../../../../shared/components/button/button.component";
import { countriesList } from "../../../../../../utils/constants/countries.constant";
import { GoogleAddress } from "../../../../../interfaces/cep";

@Component({
  selector: "app-event-address-step",
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    AddressFormComponent,
    GoogleMapsModule,
  ],
  templateUrl: "./event-address-step.component.html",
  styleUrl: "./event-address-step.component.scss",
})
export class EventAddressStepComponent implements OnInit, OnDestroy {
  private readonly googleMapService = inject(GoogleMapsService);
  private readonly mapsLoader = inject(GoogleMapsLoaderService);
  private readonly destroy$ = new Subject<void>();
  private readonly addressForm = viewChild(AddressFormComponent);

  @Input({ required: true }) formGroup!: FormGroup<AddressFormGroup>;

  center: google.maps.LatLngLiteral = { lat: -23.55052, lng: -46.633308 };
  isMapLoaded = false;
  addressSuggestions: GoogleAddress[] = [];
  showSuggestions = false;

  ngOnInit(): void {
    this.loadGoogleMaps();
    this.subscribeToAddressChange();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  subscribeToAddressChange(): void {
    this.formGroup.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(400),
        map(() => ({
          address: this.formatAddressMaps(),
          country: this.formGroup.controls.country?.value ?? "BR",
        })),
        filter(({ address }) => address.length > 5),
        switchMap(({ address, country }) => {
          return this.googleMapService.getGeoCode(address, country).pipe(first());
        }),
      )
      .subscribe((response) => {
        const results = response?.results ?? [];
        this.addressSuggestions = results;

        if (!results.length) {
          this.showSuggestions = false;
          return;
        }

        const { lat, lng } = results[0].geometry.location;
        const newMapsUrl = this.googleMapService.getMapsUrl(lat, lng);
        const currentMapsUrl = this.formGroup.controls.maps_url?.value ?? null;

        // só atualiza se mudou, pra evitar loops/dirty desnecessário
        if (currentMapsUrl !== newMapsUrl) {
          this.center = { lat, lng };
          this.formGroup.controls.maps_url?.setValue(newMapsUrl, {
            emitEvent: false,
          });
        }

        this.addressForm()?.applyGeoResult(
          this.googleMapService.extractAddressParts(results[0]),
        );

        this.showSuggestions = true;
      });
  }

  private loadGoogleMaps() {
    const apiKey = environment.GOOGLE_API_KEY_MAPS;

    this.mapsLoader
      .load(apiKey)
      .then(() => {
        this.isMapLoaded = true;
      })
      .catch((err) => {
        console.error("Erro ao carregar o Google Maps", err);
      });
  }

  formatAddressMaps(): string {
    const value = this.formGroup.getRawValue();
    const countryName =
      countriesList.find((country) => country.acronym === value.country)?.name ??
      value.country;

    const parts = [
      value.location_name,
      value.street,
      value.number,
      value.district,
      value.city_name,
      value.state,
      value.postal_code,
      countryName,
    ]
      .map((p) => String(p ?? "").trim())
      .filter(Boolean);

    return parts.join(" ");
  }

  clearAddress(): void {
    this.formGroup.patchValue(
      {
        street: "",
        district: "",
        city_name: "",
        state: "",
        postal_code: "",
        maps_url: "",
      },
      { emitEvent: false },
    );

    this.showSuggestions = false;
    this.addressSuggestions = [];
  }
}
